import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, NgForm, Validators } from '@angular/forms';
import { City } from 'src/app/interfaces/city';
import { Observable } from 'rxjs';
import { DatePipe } from '@angular/common';
import { consentValidator, insValidator, phoneNumberValidator } from 'src/app/directives/patient-validator.directive';
import { Directory } from 'src/app/interfaces/directory';
import { debounceTime, distinctUntilChanged, map, startWith } from 'rxjs/operators';
import { DirectoryService } from 'src/app/services/api/directory.service';
import { LocalisationService } from 'src/app/services/localisation.service';
import { LoadingService } from 'src/app/services/loading.service';
import { DownloadService } from 'src/app/services/download.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-add-patient-directory',
  templateUrl: './add-patient-directory.component.html',
  styleUrls: ['./add-patient-directory.component.scss'],
  providers: [DatePipe]
})
export class AddPatientDirectoryComponent implements OnInit, OnDestroy {

    public errorMessage: string = '';
    public errorType: string = '';

    public errorMessageLoadPatient: string = '';
    public errorTypeLoadPatient: string = '';

    public patientForm: FormGroup | undefined;

    cities: City[] = [];

    filteredOptions: Observable<City[]> | undefined;
    closeResult = '';

    selectedCity: string = '';

    maxDate = new Date();

    @ViewChild('formDirective') private formDirective!: NgForm;

    @ViewChild('uploadFileInput') fileInput!: ElementRef;

    resSuccess: string[] = [];
    resError: string[] = [];

    fileUpload: boolean = false;

    constructor(public directoryService: DirectoryService,
                private datePipe: DatePipe,
                private downloadService: DownloadService,
                public loader: LoadingService,
                private translate: TranslateService,
                private localisationService: LocalisationService)
    {}

    ngOnDestroy(): void {
      this.patientForm?.reset();
    }

    ngOnInit(): void {

        this.patientForm = new FormGroup({
            lname: new FormControl('', [Validators.required, Validators.pattern("^[a-zA-ZÀ-ÿ' -]+$")]),
            fname: new FormControl('', [Validators.required,Validators.pattern("^[a-zA-ZÀ-ÿ' -]+$")]),
            email: new FormControl('', [Validators.required, Validators.email, Validators.pattern('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,4}$')]),
            phoneNumber: new FormControl('', [Validators.required, phoneNumberValidator()]),
            city: new FormControl('',Validators.pattern("^[a-zA-ZÀ-ÿ' -]+$")),
            consent: new FormControl(false,[Validators.required, consentValidator()]),
            ins: new FormControl('', [Validators.required, insValidator()])
        });


        this.patientForm.addControl('dateOfBirth', new FormControl(''));

        this.filteredOptions = this.patientForm.controls.city.valueChanges.pipe(
            startWith(''),
            debounceTime(500),
            distinctUntilChanged(),
            map((value) => value && value.length >= 1 ? this._filter(value) : [])
        );

    }

    private _filter(value: string): City[] {
        this.cities = [];
        if (value.length >= 3) {
            const filterValue = value.toLowerCase();
            if (filterValue.match(/\d{5}/gm)) {
                if (filterValue == '98000') {
                    var monaco = <City>{};
                    monaco.nom = 'Monaco';
                    this.cities.push(monaco);
                    return this.cities;
                } else {
                    this.localisationService.getCitiesByPostalCode(filterValue).subscribe((res) => {
                        res.forEach((cityRes) => {
                            var city = <City>{};
                            city.nom = cityRes.nom;
                            this.cities.push(city);
                        });

                        return this.cities;
                    });
                }
            } else {
                if (filterValue.match(/monaco/gm)) {
                    var monaco = <City>{};
                    monaco.nom = 'Monaco';
                    this.cities.push(monaco);
                    return this.cities;
                } else {
                    this.localisationService.getCities(filterValue).subscribe((res) => {
                        res.forEach((cityRes) => {
                            var city = <City>{};
                            city.nom = cityRes.nom;
                            this.cities.push(city);
                        });
                        return this.cities;
                    });
                }
            }
        } else {
            this.cities = [];
        }
        return this.cities;
    }

    createPatient(patient: Directory) {
        if (patient.dateOfBirth != '') {
            patient.dateOfBirth = this.datePipe.transform(patient.dateOfBirth, 'yyyy-MM-dd');
        } else {
            patient.dateOfBirth = this.datePipe.transform('', 'yyyy-MM-dd');
        }

        patient.consentDate = this.datePipe.transform(new Date(), 'yyyy-MM-dd HH:mm:ss');

        this.directoryService.addPatient(patient).subscribe((res) => {
            if (res.result == 'SUCCESS') {
                this.errorMessage = res.content;
                this.errorType = res.result;
                this.formDirective.resetForm();
            } else {
                this.errorMessage = res.content;
                this.errorType = res.result;
            }
        });
    }

    public hasError = (controlName: string, errorName: string) => {
        return this.patientForm?.controls[controlName].hasError(errorName);
    }

    cityClick(event: any) {
        this.selectedCity = event.option.value;
    }

    checkCity() {
        setTimeout(()=> {
            if (!this.selectedCity || this.selectedCity !== this.patientForm?.controls['city'].value) {
                if (this.patientForm?.controls['city'].value.match(/\d{5}/gm)) {
                    this.patientForm?.controls['city'].setValue(null);
                    this.selectedCity = '';
                }
            }
        }, 500);
    }

    downloadLoadPatient() {
        const filename = "loadpatient.xlsx";
        this.loader.show("OPEN.DOWNLOAD-ATTACHMENT", {filename :  filename});
        this.downloadService.downloadFile(filename).subscribe(blob => {
            const a = document.createElement('a')
            const objectUrl = URL.createObjectURL(blob)
            a.href = objectUrl
            a.download = filename;
            a.click();
            URL.revokeObjectURL(objectUrl);
        })
        this.loader.hide();
    }

    async loadPatient(attachment: any)
    {
        if (attachment.files.length > 0) {
            //@ts-ignore
            Array.from(attachment.files).forEach((file: File) => {
                let fileReader = new FileReader();
                fileReader.onload = async (e) => {
                    let text = fileReader.result;
                    this.loader.show("SPINNER-LOADER.LOADPATIENT", {filename : file.name});
                    const res = await this.directoryService.loadPatient(text, file.name);
                    this.loader.hide();
                    if (res.result == 'SUCCESS') {
                        this.resError = [];
                        this.resSuccess = [];
                        var line = 2;
                        for (const [key, loadPatientResult] of Object.entries(res.content)) {

                            if (loadPatientResult) {
                                //@ts-ignore
                                for (const [resultPatient, textResult] of Object.entries(loadPatientResult)) {
                                    var msg = '[Ligne ' + line + '] ';
                                    if (resultPatient == "SUCCESS") {
                                        //@ts-ignore
                                        var split = textResult.split("]");
                                        var msgs = split[1].trim().split(";");
                                        const patientMail = msgs[msgs.length - 1];
                                        msgs.pop();
                                        this.translate.get(msgs, {patient: patientMail}).subscribe(res => {
                                            for(const [errorName, errorValue] of Object.entries(res)) {
                                                msg = msg + errorValue + " ";
                                            }
                                            //@ts-ignore
                                            this.resSuccess.push(msg.trim());
                                        });
                                    } else {
                                        //@ts-ignore
                                        var split = textResult.split("]");
                                        var msgs = split[1].trim().split(";");
                                        const patientMail = msgs[msgs.length - 1];
                                        msgs.pop();
                                        this.translate.get(msgs, {patient: patientMail}).subscribe(res => {
                                            for(const [errorName, errorValue] of Object.entries(res)) {
                                                msg = msg + errorValue + " ";
                                            }
                                            //@ts-ignore
                                            this.resError.push(msg.trim());
                                        });
                                    }

                                }
                            }
                            line = line + 1;
                        }

                    } else {
                        this.errorMessageLoadPatient = res.content;
                        this.errorTypeLoadPatient = res.result;
                    }
                }
                fileReader.readAsDataURL(file);
            });
        } else {
            this.errorMessageLoadPatient = "LOADPATIENT.ERRORS.NO-FILE";
            this.errorTypeLoadPatient = "ERROR";
        }
    }

    fileUpdate() {
        if (this.fileInput.nativeElement.files.length > 0) {
            this.fileUpload = true;
        } else {
            this.fileUpload = false;
        }
    }
}
